import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button, Input, Label, BrandMark } from "../components/ui-kit";
import { ArrowRight } from "@phosphor-icons/react";
import PasswordInput from "../components/PasswordInput";
import OAuthButtons from "../components/OAuthButtons";

export default function Login() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const error = searchParams.get("oauth_error");
    if (error) toast.error("Connexion directe indisponible. Verifiez la configuration du provider.");
  }, [searchParams]);

  const goToRole = (role) => {
    if (role === "driver") navigate("/driver", { replace: true });
    else if (role === "courier") navigate("/courier", { replace: true });
    else if (role === "merchant") navigate("/merchant", { replace: true });
    else if (role === "agent") navigate("/agent", { replace: true });
    else if (role === "admin") navigate("/admin", { replace: true });
    else navigate("/app", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      if (data.verification_required) {
        setChallenge(data);
        toast.success("Code de connexion envoye par email");
        return;
      }
      await refresh();
      toast.success("Bienvenue sur NEXORA-CAB");
      if (data.role !== "admin" && !data.email_verified) {
        navigate("/verify-contact", { replace: true });
        return;
      }
      goToRole(data.role);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmLogin = async (e) => {
    e.preventDefault();
    if (!challenge?.login_challenge_id) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login/confirm", {
        login_challenge_id: challenge.login_challenge_id,
        code,
      });
      await refresh();
      toast.success("Connexion verifiee");
      goToRole(data.role);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (challenge) {
    return (
      <div className="relative app-screen bg-premium-grad grain text-foreground">
        <div className="mx-auto flex app-screen max-w-md flex-col items-center justify-center px-5 auth-screen-safe">
          <Link to="/" className="mb-10" data-testid="brand-back-home">
            <BrandMark />
          </Link>
          <div className="w-full rounded-3xl glass-strong p-6 glow-blue">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-primary">Etape 2/2</div>
            <h1 className="mt-1 font-heading text-3xl font-bold">Confirmer la connexion</h1>
            <div className="mt-3 rounded-2xl border border-primary/30 bg-primary/10 p-4 text-sm text-muted-foreground">
              Un code de securite a ete envoye a <span className="font-bold text-foreground">{challenge.email}</span>.
            </div>
            <form onSubmit={confirmLogin} className="mt-5 space-y-4" data-testid="login-confirm-form">
              <div>
                <Label>Code de verification</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  required
                  data-testid="login-verification-code"
                />
              </div>
              <Button
                type="submit"
                variant="secondary"
                className="w-full py-4 text-base"
                disabled={submitting || !code}
                data-testid="login-confirm-submit"
              >
                {submitting ? "Verification..." : "Confirmer et entrer"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setChallenge(null);
                  setCode("");
                }}
              >
                Changer de compte
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative app-screen bg-premium-grad grain text-foreground">
      <div className="mx-auto flex app-screen max-w-md flex-col items-center justify-center px-5 auth-screen-safe">
        <Link to="/" className="mb-10" data-testid="brand-back-home">
          <BrandMark />
        </Link>

        <div className="w-full rounded-3xl glass-strong p-6 glow-blue">
          <div className="mb-5">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-primary">Étape 1/1</div>
            <h1 className="font-heading text-3xl font-bold">Connexion</h1>
            <div className="mt-1 text-sm text-muted-foreground">
              Entrez votre email ou numéro +243
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label>Email ou Téléphone</Label>
              <Input
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Nom d'utilisateur ou email"
                autoComplete="username"
                required
                data-testid="login-identifier"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Mot de passe</Label>
                <Link to="/forgot-password" className="text-[10px] font-bold uppercase tracking-widest text-primary">
                  Oublié ?
                </Link>
              </div>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mot de passe"
                autoComplete="current-password"
                required
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              className="w-full py-4 text-base"
              disabled={submitting}
              data-testid="login-submit"
            >
              {submitting ? "Connexion..." : (<>Se connecter <ArrowRight weight="bold" /></>)}
            </Button>
          </form>

          <div className="mt-5">
            <OAuthButtons />
          </div>

          <div className="mt-6 border-t border-white/10 pt-4 text-center text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Pas encore inscrit ?{" "}
            <Link
              to="/register"
              className="text-primary hover:brightness-110"
              data-testid="go-register"
            >
              Créer un compte
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
